import tpvPreview from '@assets/images/tpv-hero.webp';
import userProfileIllustration from '@assets/images/user-profile-illustration.svg';
import comandaCocinaVideo from '@assets/videos/Enviar comanda + pantalla de cocina.webm';
import gestionInventarioVideo from '@assets/videos/Gestión de inventario.webm';
import pdasCocinaVideo from '@assets/videos/PDAs y monitor de cocina.webm';
import registrarVentaVideo from '@assets/videos/Registrar venta.webm';
import { useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { HeroTitle, HERO_TITLE_LETTER_COUNT } from '../../components/marketing/HeroTitle';
import { LoopingVideo } from '../../components/marketing/LoopingVideo';
import { MarketingFaq } from '../../components/marketing/MarketingFaq';
import { ProductIcon } from '../../components/marketing/ProductIcon';
import { usePricing } from '../../hooks/usePricing';
import { useMarketingMetadata } from '../../hooks/useMarketingMetadata';

const demos = [
  { title: 'Toma el pedido en la mesa', description: 'Anota la comanda desde la PDA y sigue su preparación en cocina.', video: pdasCocinaVideo },
  { title: 'Envía la comanda a cocina', description: 'El equipo recibe los pedidos en su pantalla y gestiona lo que queda por preparar.', video: comandaCocinaVideo },
  { title: 'Registra la venta y cobra', description: 'Gestiona las ventas de barra y las cuentas de las mesas desde el TPV.', video: registrarVentaVideo },
  { title: 'Lleva al día tu inventario', description: 'Consulta tus productos y controla el stock desde el mismo programa.', video: gestionInventarioVideo },
];

const included = [
  'Ventas, mesas e inventario',
  'Aplicación para PDA y pantalla de cocina',
  'Cierres e informes sincronizados en la nube',
  'VeriFactu y actualizaciones de Hosty',
];

const steps = [
  { title: 'Crea tu cuenta', description: 'Contrata una licencia por cada TPV principal. El software de PDA y cocina está incluido.' },
  { title: 'Prepara tu local', description: 'Instala Hosty en Windows y configura productos, mesas y datos fiscales. Si necesitas ayuda, te damos presupuesto.' },
  { title: 'Empieza el servicio', description: 'Toma pedidos, coordina la cocina y cobra. Consulta tus cierres desde el panel al sincronizar el TPV.' },
];

// Se conserva la sección para poder reactivarla sin reconstruir su contenido.
const SHOW_DEVELOPER_PROFILE = false;

export function HomePage() {
  const pricing = usePricing();
  const [introSkipped, setIntroSkipped] = useState(false);
  useMarketingMetadata('TPV Hosty | Sala, cocina y caja conectadas', 'Software TPV para bares, restaurantes y cafeterías. Ventas, PDA, cocina, inventario e informes en un solo plan, con VeriFactu incluido.');

  return (
    <div className="hosty-home">
      <section
        className={`hero home-hero${introSkipped ? ' home-hero--intro-skipped' : ''}`}
        aria-labelledby="home-title"
        style={{ '--hero-letter-count': HERO_TITLE_LETTER_COUNT } as CSSProperties}
        onFocusCapture={() => setIntroSkipped(true)}
      >
        <div className="container hero__grid">
          <div className="home-hero__copy">
            <span className="marketing-eyebrow hero-reveal">Software TPV para hostelería</span>
            <HeroTitle />
            <p className="hero__lead hero-reveal">Del pedido en la mesa al cierre de caja. Atiende tu bar, restaurante o cafetería con un mismo programa para todo el equipo.</p>
            <div className="home-hero__offer hero-reveal">
              <p><strong>{pricing.amount} €</strong><span>/mes por TPV principal</span></p>
              <span>IVA incluido · Todos los módulos, también VeriFactu</span>
            </div>
            <div className="hero__actions hero-reveal">
              <Link to="/registro" className="ui-btn ui-btn--primary ui-btn--lg">Crear mi cuenta <ProductIcon name="arrow" /></Link>
              <a href="#tpv-en-accion" className="ui-btn ui-btn--ghost ui-btn--ghost-white ui-btn--lg"><ProductIcon name="play" /> Ver el TPV en acción</a>
            </div>
            <p className="hero__reassurance hero-reveal">Equipos, instalación y soporte técnico aparte. Pagos integrados opcionales, con tarifa por operación. <Link to="/precios">Ver condiciones</Link></p>
          </div>
          <figure className="hero-preview home-hero__media hero-reveal">
            <img src={tpvPreview} alt="TPV Hosty con la pantalla de venta, productos y cuenta del cliente" className="hero-preview__image" width={900} height={900} fetchPriority="high" />
            <figcaption><ProductIcon name="desktop" /><span>Windows 10 / 11 · 64 bits<small>El equipo físico no está incluido.</small></span></figcaption>
          </figure>
        </div>
        <div className="home-hero__scroll hero-reveal">
          <a href="#tpv-en-accion" className="home-hero__scroll-link" aria-label="Descubre más: ver el TPV en acción">
            <span>Descubre más</span>
            <span className="home-hero__scroll-arrow"><ProductIcon name="arrow" /></span>
          </a>
        </div>
      </section>

      <section className="section home-demos" id="tpv-en-accion" aria-labelledby="demos-title">
        <div className="container">
          <div className="section__header home-section-heading">
            <div><span className="marketing-eyebrow">Así se trabaja con Hosty</span><h2 id="demos-title">Un servicio completo.<br />De mesa a cocina y caja.</h2></div>
            <p className="muted">Mira el programa por dentro, con ejemplos reales de las tareas de cada día.</p>
          </div>
          <div className="demo-showcase-grid">
            {demos.map((demo, index) => (
              <article key={demo.title} className="demo-showcase-card">
                <div className="home-demo__heading"><span aria-hidden="true">0{index + 1}</span><div><h3>{demo.title}</h3><p>{demo.description}</p></div></div>
                <div className="demo-showcase-card__media"><LoopingVideo src={demo.video} alt={demo.title + ': demostración de TPV Hosty'} /></div>
              </article>
            ))}
          </div>
          <div className="home-demos__footer">
            <p>¿Quieres explorar también la gestión web?</p>
            <Link to="/login?demo=1" className="marketing-text-link">
              Abrir demo del panel <ProductIcon name="arrow" />
            </Link>
          </div>
        </div>
      </section>

      <section className="section home-plan" aria-labelledby="home-plan-title">
        <div className="container home-plan__grid">
          <div>
            <span className="marketing-eyebrow">Un solo plan</span>
            <h2 id="home-plan-title">Tu equipo completo.<br />Sin sumar módulos.</h2>
            <p className="home-plan__lead">La barra, la sala y la cocina trabajan con el mismo sistema. Tú consultas el negocio desde tu cuenta.</p>
            <ul className="home-plan__includes">
              {included.map((item) => <li key={item}><ProductIcon name="check" />{item}</li>)}
            </ul>
            <Link to="/caracteristicas" className="marketing-text-link">Conocer las funciones <ProductIcon name="arrow" /></Link>
          </div>
          <aside className="home-plan__offer" aria-label="Precio de Hosty completo">
            <span className="marketing-eyebrow">Hosty completo</span>
            <p className="home-plan__price"><strong>{pricing.amount} €</strong><span>/mes</span></p>
            <p>Por TPV principal · IVA incluido</p>
            <Link to="/precios" className="ui-btn ui-btn--primary ui-btn--lg">Ver el plan y sus condiciones <ProductIcon name="arrow" /></Link>
            <p className="home-plan__terms">Equipos, instalación e intervenciones técnicas aparte. Los pagos integrados son opcionales y tienen tarifa por operación.</p>
          </aside>
        </div>
      </section>

      <section className="section home-steps" aria-labelledby="steps-title">
        <div className="container">
          <div className="section__header">
            <span className="marketing-eyebrow">Para empezar</span>
            <h2 id="steps-title">De tu cuenta a tu primer servicio.</h2>
          </div>
          <ol className="home-steps__list">
            {steps.map((step, index) => (
              <li key={step.title}>
                <span className="home-steps__number" aria-hidden="true">0{index + 1}</span>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <MarketingFaq />

      {SHOW_DEVELOPER_PROFILE && (
        <section className="section home-about" aria-labelledby="about-title">
          <div className="container home-about__inner">
            <img src={userProfileIllustration} alt="Ilustración de usuario para el perfil de Kevin" width={176} height={176} loading="lazy" />
            <div>
              <span className="marketing-eyebrow">Detrás de Hosty</span>
              <h2 id="about-title">Hola, soy Kevin.</h2>
              <p>Soy el desarrollador de TPV Hosty. Somos un equipo pequeño y mejoramos el programa a partir de su uso real, con nuevas funciones, ajustes y correcciones.</p>
              <a href="https://www.linkedin.com/in/kevinwegenerantolinez/" target="_blank" rel="noopener noreferrer" className="marketing-text-link">
                Conóceme en LinkedIn <span className="sr-only">(se abre en otra pestaña)</span>
                <ProductIcon name="arrow" />
              </a>
            </div>
          </div>
        </section>
      )}

      <section className="cta-section home-contact" aria-labelledby="contact-title">
        <div className="container cta-section__inner">
          <div>
            <span className="marketing-eyebrow">¿Lo ponemos en marcha?</span>
            <h2 id="contact-title">Te ayudamos a preparar tu local.</h2>
            <p>Cuéntanos qué equipos tienes y qué necesitas. La instalación remota o presencial se presupuesta aparte, antes de empezar.</p>
          </div>
          <Link to="/contacto" className="ui-btn ui-btn--secondary ui-btn--lg">Consultar mi instalación <ProductIcon name="arrow" /></Link>
        </div>
      </section>
    </div>
  );
}
